import { useState, useEffect, useRef } from 'react'
import { supabase } from './supabaseClient'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function AuthPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState('login')

  async function handleSubmit() {
    setLoading(true); setError('')
    if (mode === 'login') {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) setError(error.message)
    } else {
      const { error } = await supabase.auth.signUp({ email, password })
      if (error) setError(error.message)
      else setError('Check your email to confirm registration')
    }
    setLoading(false)
  }

  return (
    <div style={S.authWrap}>
      <div style={S.authBox}>
        <div style={S.authMark}>
          <div style={S.authMarkInner}/>
        </div>
        <div style={S.authTitle}>SITE<span style={{color:C.amber}}>AI</span></div>
        <div style={S.authSub}>Construction Management</div>
        <div style={S.authFields}>
          <div style={S.fieldWrap}>
            <label style={S.fieldLabel}>Email</label>
            <input value={email} onChange={e=>setEmail(e.target.value)}
              style={S.authInput} type="email" autoComplete="email"/>
          </div>
          <div style={S.fieldWrap}>
            <label style={S.fieldLabel}>Password</label>
            <input value={password} onChange={e=>setPassword(e.target.value)}
              style={S.authInput} type="password"
              onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
          </div>
          {error && <div style={S.authError}>{error}</div>}
          <button onClick={handleSubmit} disabled={loading} style={S.authBtn}>
            {loading ? 'Working...' : mode==='login' ? 'Sign in' : 'Create account'}
          </button>
          <button onClick={()=>setMode(mode==='login'?'register':'login')} style={S.authSwitch}>
            {mode==='login' ? 'No account? Register' : 'Have account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('dashboard')
  const [sites, setSites] = useState([])
  const [activeSite, setActiveSite] = useState(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session); setLoading(false)
    })
    supabase.auth.onAuthStateChange((_event, sess) => setSession(sess))
  }, [])

  useEffect(() => { if (session) loadSites() }, [session])

  async function loadSites() {
    const { data } = await supabase.from('sites')
      .select('*, site_materials(*)')
      .order('created_at', { ascending: false })
    if (data) setSites(data)
  }

  if (loading) return <Splash />
  if (!session) return <AuthPage />

  const activeSites = sites.filter(s => s.status === 'active' || s.status === 'pending')

  const mainContent = (
    <>
      {tab==='dashboard' && <DashboardTab sites={sites} onNavigate={setTab}/>}
      {tab==='diary'     && <DiaryTab activeSite={activeSite} sites={activeSites} onRefresh={loadSites} session={session}/>}
      {tab==='archive'   && <ArchiveTab sites={sites}/>}
      {tab==='sites'     && <SitesTab sites={sites} onRefresh={loadSites} session={session}/>}
      {tab==='qs'        && <QSTab sites={sites} activeSite={activeSite}/>}
      {tab==='ai'        && <AITab sites={sites} activeSite={activeSite}/>}
    </>
  )

  if (isMobile) {
    return (
      <div style={S.appMobile}>
        <MobileHeader activeSite={activeSite} activeSites={activeSites} setActiveSite={setActiveSite}/>
        <main style={S.mobileMain}>{mainContent}</main>
        <MobileNav tab={tab} setTab={setTab}/>
      </div>
    )
  }

  return (
    <div style={S.appDesktop}>
      <Sidebar tab={tab} setTab={setTab} sites={sites} activeSite={activeSite}
        setActiveSite={setActiveSite} session={session} onRefresh={loadSites}/>
      <main style={S.desktopMain}>
        <div style={S.desktopContent}>{mainContent}</div>
      </main>
    </div>
  )
}

function Splash() {
  return (
    <div style={S.splash}>
      <div style={{textAlign:'center'}}>
        <div style={{width:32,height:32,border:`1.5px solid ${C.amber}`,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
          <div style={{width:12,height:12,background:C.amber,borderRadius:2}}/>
        </div>
        <div style={{fontFamily:C.mono,fontSize:24,fontWeight:600,letterSpacing:'0.08em',color:C.fg}}>
          SITE<span style={{color:C.amber}}>AI</span>
        </div>
        <div style={{fontSize:11,color:C.muted,letterSpacing:'0.12em',marginTop:8,fontFamily:C.mono}}>LOADING</div>
      </div>
    </div>
  )
}

function Sidebar({ tab, setTab, sites, activeSite, setActiveSite, session }) {
  const activeSites = sites.filter(s => s.status === 'active' || s.status === 'pending')
  const navItems = [
    ['dashboard','overview'],
    ['diary','diary'],
    ['archive','archive'],
    ['sites','sites'],
    ['qs','qs report'],
    ['ai','ai manager'],
  ]
  return (
    <aside style={S.sidebar}>
      {/* Logo */}
      <div style={S.sidebarLogo}>
        <div style={{width:24,height:24,border:`1.5px solid ${C.amber}`,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          <div style={{width:9,height:9,background:C.amber,borderRadius:1.5}}/>
        </div>
        <div style={{fontFamily:C.mono,fontSize:14,fontWeight:600,letterSpacing:'0.06em',color:C.fg}}>
          SITE<span style={{color:C.amber}}>AI</span>
        </div>
      </div>

      {/* Nav */}
      <div style={S.sidebarSection}>
        <div style={S.sidebarLabel}>Navigation</div>
        {navItems.map(([id,label]) => (
          <button key={id} onClick={()=>setTab(id)}
            style={{...S.sidebarNav,...(tab===id?S.sidebarNavActive:{})}}>
            <div style={{...S.navDot,...(tab===id?{background:C.amber}:{})}}/>
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Sites */}
      <div style={{...S.sidebarSection,flex:1,overflowY:'auto'}}>
        <div style={S.sidebarLabel}>Active sites</div>
        {activeSites.length===0
          ? <div style={{fontSize:11,color:C.dim,padding:'4px 0'}}>No active sites</div>
          : activeSites.map(s => (
            <button key={s.id} onClick={()=>setActiveSite(activeSite?.id===s.id?null:s)}
              style={{...S.sidebarSite,...(activeSite?.id===s.id?S.sidebarSiteActive:{})}}>
              <div style={{width:5,height:5,borderRadius:'50%',background:activeSite?.id===s.id?C.amber:C.dim,flexShrink:0,marginTop:1}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:activeSite?.id===s.id?C.amber:C.fg2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.name}</div>
                {s.expected_end_date&&<div style={{fontSize:10,color:C.dim,marginTop:1,fontFamily:C.mono}}>> {s.expected_end_date}</div>}
              </div>
            </button>
          ))
        }
      </div>

      {/* Bottom */}
      <div style={S.sidebarBottom}>
        <div style={{fontSize:11,color:C.dim,marginBottom:8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontFamily:C.mono}}>
          {session?.user?.email}
        </div>
        <button onClick={()=>supabase.auth.signOut()} style={S.sidebarLogout}>sign out</button>
      </div>
    </aside>
  )
}

function MobileHeader({ activeSite, activeSites, setActiveSite }) {
  return (
    <header style={S.mobileHeader}>
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:20,height:20,border:`1.5px solid ${C.amber}`,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{width:7,height:7,background:C.amber,borderRadius:1}}/>
        </div>
        <span style={{fontFamily:C.mono,fontSize:13,fontWeight:600,letterSpacing:'0.06em',color:C.fg}}>
          SITE<span style={{color:C.amber}}>AI</span>
        </span>
      </div>
      <select value={activeSite?.id||''} onChange={e=>setActiveSite(activeSites.find(s=>s.id===e.target.value)||null)}
        style={S.mobileHeaderSelect}>
        <option value=''>- site -</option>
        {activeSites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </header>
  )
}

function MobileNav({ tab, setTab }) {
  const items = [
    ['dashboard','*','home'],
    ['diary','=','diary'],
    ['archive','#','archive'],
    ['sites','o','sites'],
    ['qs','%','qs'],
    ['ai','+','ai'],
  ]
  return (
    <nav style={S.mobileNav}>
      {items.map(([id,icon,label])=>(
        <button key={id} onClick={()=>setTab(id)}
          style={{...S.mobileNavBtn,...(tab===id?S.mobileNavActive:{})}}>
          <span style={{fontSize:16,lineHeight:1}}>{icon}</span>
          <span style={{fontSize:9,marginTop:2,fontFamily:C.mono,letterSpacing:'0.05em'}}>{label}</span>
        </button>
      ))}
    </nav>
  )
}

const SITE_STATUSES = [
  { value:'active',    label:'Active',    color:'#34d399' },
  { value:'pending',   label:'Pending',   color:'#f5a623' },
  { value:'complete',  label:'Complete',  color:'#64748b' },
  { value:'claimed',   label:'Claimed',   color:'#a78bfa' },
]

function StatusBadge({ status }) {
  const cfg = SITE_STATUSES.find(s=>s.value===status)||SITE_STATUSES[1]
  return (
    <span style={{
      fontFamily:C.mono, fontSize:9, fontWeight:600, letterSpacing:'0.1em',
      padding:'3px 7px', borderRadius:3, textTransform:'uppercase',
      color:cfg.color, border:`0.5px solid ${cfg.color}44`,
      background:`${cfg.color}0d`
    }}>
      {cfg.label}
    </span>
  )
}

function DashboardTab({ sites, onNavigate }) {
  const [todayEntries, setTodayEntries] = useState([])
  const [monthPurchases, setMonthPurchases] = useState(0)
  const today = new Date().toISOString().split('T')[0]
  const monthStart = today.slice(0,7) + '-01'

  useEffect(() => { loadData() }, [sites])

  async function loadData() {
    const { data: entries } = await supabase.from('diary_entries').select('site_id').eq('date', today)
    setTodayEntries(entries||[])
    const { data: purchases } = await supabase.from('purchases').select('total_amount').gte('created_at', monthStart)
    setMonthPurchases((purchases||[]).reduce((s,p)=>s+(p.total_amount||0),0))
  }

  const activeSites = sites.filter(s=>s.status==='active'||s.status==='pending')
  const todayWritten = new Set(todayEntries.map(e=>e.site_id))
  const missingDiary = activeSites.filter(s=>!todayWritten.has(s.id))
  const allMats = activeSites.flatMap(s=>(s.site_materials||[]).map(m=>({...m,siteName:s.name})))
  const warningMats = allMats.filter(m=>m.planned_qty>0&&(m.used_qty/m.planned_qty)>=0.8)

  return (
    <div style={S.sec}>
      {/* Header row */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:28}}>
        <div>
          <div style={S.pageTitle}>Overview</div>
          <div style={{fontFamily:C.mono,fontSize:11,color:C.dim,marginTop:4}}>{today}</div>
        </div>
        <div style={{fontFamily:C.mono,fontSize:10,color:C.dim,letterSpacing:'0.1em'}}>
          {activeSites.length} ACTIVE
        </div>
      </div>

      {/* Stat row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8,marginBottom:20}}>
        {[
          ['Active', sites.filter(s=>s.status==='active').length, '#34d399'],
          ['Pending', sites.filter(s=>s.status==='pending').length, C.amber],
          ['Complete', sites.filter(s=>s.status==='complete').length, '#64748b'],
          ['Month spend', `RM ${(monthPurchases/1000).toFixed(0)}k`, '#38bdf8'],
        ].map(([label,value,color])=>(
          <div key={label} style={S.statCard}>
            <div style={{fontSize:10,color:C.dim,fontFamily:C.mono,letterSpacing:'0.08em',marginBottom:8}}>{label.toUpperCase()}</div>
            <div style={{fontSize:22,fontWeight:500,color,fontFamily:C.mono}}>{value}</div>
          </div>
        ))}
      </div>

      {/* Diary alert */}
      {missingDiary.length>0 && (
        <div style={{...S.card,borderColor:'#ef444422',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:'#ef4444'}}/>
            <span style={{fontFamily:C.mono,fontSize:10,color:'#ef4444',letterSpacing:'0.1em'}}>
              NO DIARY TODAY - {missingDiary.length} SITE{missingDiary.length>1?'S':''}
            </span>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {missingDiary.map(s=>(
              <button key={s.id} onClick={()=>onNavigate('diary')}
                style={{fontSize:11,padding:'4px 10px',background:'transparent',border:'0.5px solid #ef444433',borderRadius:4,color:'#ef4444',cursor:'pointer',fontFamily:C.mono}}>
                {s.name} >
              </button>
            ))}
          </div>
        </div>
      )}
      {missingDiary.length===0&&activeSites.length>0&&(
        <div style={{...S.card,borderColor:'#34d39922',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:'#34d399'}}/>
            <span style={{fontSize:12,color:'#34d399'}}>All active sites have diary entries today</span>
          </div>
        </div>
      )}

      {/* Material warnings */}
      {warningMats.length>0&&(
        <div style={{...S.card,borderColor:'#f5a62322',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:C.amber}}/>
            <span style={{fontFamily:C.mono,fontSize:10,color:C.amber,letterSpacing:'0.1em'}}>MATERIAL WARNING</span>
          </div>
          {warningMats.map((m,i)=>{
            const pct=Math.min(100,Math.round(m.used_qty/m.planned_qty*100))
            return (
              <div key={i} style={{marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:4}}>
                  <span style={{color:C.fg2}}><span style={{color:C.dim}}>{m.siteName} / </span>{m.material_name}</span>
                  <span style={{fontFamily:C.mono,color:pct>=100?'#ef4444':C.amber}}>{pct}%{pct>=100?' EXCEEDED':''}</span>
                </div>
                <div style={{height:2,background:C.border,borderRadius:1}}>
                  <div style={{height:'100%',background:pct>=100?'#ef4444':C.amber,borderRadius:1,width:`${pct}%`}}/>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Sites progress */}
      {activeSites.length>0&&(
        <div style={S.card}>
          <div style={{fontFamily:C.mono,fontSize:10,color:C.dim,letterSpacing:'0.1em',marginBottom:16}}>ACTIVE SITES</div>
          {activeSites.map((s,idx)=>{
            const mats=s.site_materials||[]
            const wrote=todayWritten.has(s.id)
            return (
              <div key={s.id} style={{marginBottom:idx<activeSites.length-1?20:0,paddingBottom:idx<activeSites.length-1?20:0,borderBottom:idx<activeSites.length-1?`0.5px solid ${C.border}`:undefined}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                  <span style={{fontSize:13,fontWeight:500,color:C.fg}}>{s.name}</span>
                  <StatusBadge status={s.status}/>
                  <span style={{fontFamily:C.mono,fontSize:9,color:wrote?'#34d399':'#ef4444',letterSpacing:'0.08em'}}>
                    {wrote?'+ diary':'- no diary'}
                  </span>
                  {s.expected_end_date&&<span style={{fontFamily:C.mono,fontSize:10,color:C.dim,marginLeft:'auto'}}>> {s.expected_end_date}</span>}
                </div>
                {mats.length===0
                  ? <div style={{fontSize:11,color:C.dim}}>No materials tracked</div>
                  : mats.map(m=>{
                    const pct=m.planned_qty>0?Math.min(100,Math.round(m.used_qty/m.planned_qty*100)):0
                    return (
                      <div key={m.id} style={{marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3}}>
                          <span style={{color:C.fg2}}>{m.material_name}</span>
                          <span style={{fontFamily:C.mono,color:C.dim}}>{m.used_qty}/{m.planned_qty} {m.unit}</span>
                        </div>
                        <div style={{height:2,background:C.border,borderRadius:1}}>
                          <div style={{height:'100%',background:pct>=100?'#ef4444':pct>=80?C.amber:'#34d399',borderRadius:1,width:`${pct}%`}}/>
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            )
          })}
        </div>
      )}
      {activeSites.length===0&&<div style={S.empty}>No active sites - go to Sites to create one.</div>}
    </div>
  )
}
function EntryImages({ entryId }) {
  const [images, setImages] = useState([])
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => { loadImages() }, [entryId])

  async function loadImages() {
    const { data } = await supabase.from('entry_images')
      .select('*').eq('diary_entry_id', entryId).order('created_at')
    if (!data?.length) return
    const withUrls = await Promise.all(data.map(async img => {
      const { data: urlData } = await supabase.storage
        .from('site-images').createSignedUrl(img.storage_path, 3600)
      return { ...img, url: urlData?.signedUrl }
    }))
    setImages(withUrls.filter(i => i.url))
  }

  if (!images.length) return null

  return (
    <div style={{marginTop:16,paddingTop:14,borderTop:`0.5px solid ${C.border}`}}>
      <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em',marginBottom:10}}>
        PHOTOS ({images.length})
      </div>
      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
        {images.map(img => (
          <div key={img.id} style={{cursor:'pointer'}} onClick={()=>setLightbox(img)}>
            <img src={img.url} alt={img.caption||''}
              style={{width:110,height:80,objectFit:'cover',borderRadius:4,border:`0.5px solid ${C.border}`,display:'block'}}/>
            {img.caption&&(
              <div style={{fontSize:10,color:C.dim,marginTop:3,maxWidth:110,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontFamily:C.mono}}>
                {img.caption}
              </div>
            )}
          </div>
        ))}
      </div>
      {lightbox&&(
        <div onClick={()=>setLightbox(null)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.95)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',padding:20,cursor:'pointer'}}>
          <img src={lightbox.url} alt={lightbox.caption||''}
            style={{maxWidth:'100%',maxHeight:'80vh',objectFit:'contain',borderRadius:4}}/>
          {lightbox.caption&&<div style={{color:C.fg2,fontSize:12,marginTop:12,fontFamily:C.mono}}>{lightbox.caption}</div>}
          <div style={{color:C.dim,fontSize:10,marginTop:8,fontFamily:C.mono,letterSpacing:'0.1em'}}>CLICK TO CLOSE</div>
        </div>
      )}
    </div>
  )
}

function ArchiveEditInline({ entry, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [processing, setProcessing] = useState(false)
  const [newOutput, setNewOutput] = useState('')
  const [saving, setSaving] = useState(false)

  function startEdit() { setText(entry.raw_input||''); setNewOutput(''); setEditing(true) }

  async function reprocess() {
    if (!text.trim()) return
    setProcessing(true); setNewOutput('')
    const apiKey = process.env.REACT_APP_ANTHROPIC_KEY
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{'Content-Type':'application/json','x-api-key':apiKey,'anthropic-version':'2023-06-01','anthropic-dangerous-direct-browser-access':'true'},
        body: JSON.stringify({
          model:'claude-sonnet-4-20250514', max_tokens:1500,
          system:`You are a professional construction site diary AI for a contractor in Malaysia. Format the diary entry cleanly using markdown tables and sections. Only include sections with actual content. Always end with 🔔 **风险提醒:** [1 line]`,
          messages:[{role:'user',content:`Reformat this site diary entry:\n\n${text}`}]
        })
      })
      const d = await res.json()
      setNewOutput(d.content?.[0]?.text||'')
    } catch(e) { setNewOutput('Error: '+e.message) }
    setProcessing(false)
  }

  async function handleSave() {
    setSaving(true)
    const finalOutput = newOutput || entry.ai_output
    await supabase.from('diary_entries').update({
      raw_input: text, ai_output: finalOutput, updated_at: new Date().toISOString()
    }).eq('id', entry.id)

    if (newOutput && entry.site_id) {
      const apiKey = process.env.REACT_APP_ANTHROPIC_KEY
      await updateMaterialUsage(entry.site_id, newOutput, apiKey, entry.id, true)
    }
    onSaved(); setEditing(false); setNewOutput('')
    setSaving(false)
  }

  if (!editing) {
    return (
      <button onClick={startEdit} style={{...S.btnGhost,fontSize:11,fontFamily:C.mono,letterSpacing:'0.05em'}}>
        edit entry
      </button>
    )
  }

  return (
    <div>
      <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em',marginBottom:8}}>EDIT RAW INPUT</div>
      <textarea value={text} onChange={e=>setText(e.target.value)}
        style={{...S.textarea,minHeight:90,marginBottom:10}}/>
      <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
        <button onClick={reprocess} disabled={processing} style={S.btnAmber}>
          {processing?'processing...':'re-process'}
        </button>
        <button onClick={()=>setEditing(false)} style={S.btnGhost}>cancel</button>
      </div>
      {newOutput&&(
        <div style={{...S.card,borderColor:'#f5a62322',marginBottom:10}}>
          <div style={{fontFamily:C.mono,fontSize:9,color:C.amber,letterSpacing:'0.1em',marginBottom:10}}>PREVIEW</div>
          <div dangerouslySetInnerHTML={{__html:mdToHtml(newOutput)}}/>
          <button onClick={handleSave} disabled={saving} style={{...S.btnGreen,marginTop:12}}>
            {saving?'saving...':'save changes'}
          </button>
        </div>
      )}
      {!newOutput&&(
        <button onClick={handleSave} disabled={saving} style={S.btnGreen}>
          {saving?'saving...':'save text only'}
        </button>
      )}
    </div>
  )
}

function ArchiveTab({ sites }) {
  const [entries, setEntries] = useState([])
  const [fSite, setFSite] = useState('')
  const [fFrom, setFFrom] = useState('')
  const [fTo, setFTo] = useState('')
  const [fKw, setFKw] = useState('')
  const [expanded, setExpanded] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(()=>{ loadEntries() },[fSite,fFrom,fTo])

  async function loadEntries() {
    setLoading(true)
    let q = supabase.from('diary_entries').select('*, sites(name)').order('date',{ascending:false})
    if (fSite) q = q.eq('site_id',fSite)
    if (fFrom) q = q.gte('date',fFrom)
    if (fTo) q = q.lte('date',fTo)
    const { data } = await q
    setEntries(data||[]); setLoading(false)
  }

  const filtered = fKw
    ? entries.filter(e=>(e.ai_output||'').toLowerCase().includes(fKw.toLowerCase())||(e.raw_input||'').toLowerCase().includes(fKw.toLowerCase()))
    : entries

  return (
    <div style={S.sec}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:28}}>
        <div style={S.pageTitle}>Archive</div>
        <div style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{filtered.length} ENTRIES</div>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        <select value={fSite} onChange={e=>setFSite(e.target.value)} style={{...S.input,flex:'1 1 130px'}}>
          <option value=''>All sites</option>
          {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type='date' value={fFrom} onChange={e=>setFFrom(e.target.value)} style={{...S.input,flex:'1 1 120px'}}/>
        <input type='date' value={fTo} onChange={e=>setFTo(e.target.value)} style={{...S.input,flex:'1 1 120px'}}/>
        <input placeholder='Search...' value={fKw} onChange={e=>setFKw(e.target.value)} style={{...S.input,flex:'1 1 120px'}}/>
        <button onClick={()=>{setFSite('');setFFrom('');setFTo('');setFKw('')}} style={S.btnGhost}>clear</button>
      </div>
      {loading&&<div style={S.empty}>Loading...</div>}
      {!loading&&filtered.length===0&&<div style={S.empty}>No entries found.</div>}
      {filtered.map(entry=>(
        <div key={entry.id} style={{...S.card,marginBottom:6,...(expanded===entry.id?{borderColor:'#f5a62322'}:{})}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}}
            onClick={()=>setExpanded(expanded===entry.id?null:entry.id)}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:5,height:5,borderRadius:'50%',background:expanded===entry.id?C.amber:C.dim,flexShrink:0}}/>
              <span style={{fontSize:13,fontWeight:500,color:C.fg}}>{entry.sites?.name||'Unknown'}</span>
              <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{entry.date}</span>
            </div>
            <span style={{fontFamily:C.mono,fontSize:12,color:C.dim}}>{expanded===entry.id?'-':'+'}</span>
          </div>
          {expanded===entry.id&&(
            <div style={{marginTop:16,paddingTop:16,borderTop:`0.5px solid ${C.border}`}}>
              <div dangerouslySetInnerHTML={{__html:mdToHtml(entry.ai_output||'')}}/>
              <EntryImages entryId={entry.id}/>
              <div style={{marginTop:14,paddingTop:14,borderTop:`0.5px solid ${C.border}`}}>
                <ArchiveEditInline entry={entry} onSaved={loadEntries}/>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function DiaryTab({ activeSite, sites, onRefresh, session }) {
  const [text, setText] = useState('')
  const [selectedSite, setSelectedSite] = useState(activeSite?.id||'')
  const [dateVal, setDateVal] = useState(new Date().toISOString().split('T')[0])
  const [receiptImages, setReceiptImages] = useState([])
  const [progressImages, setProgressImages] = useState([])
  const [progressCaptions, setProgressCaptions] = useState([])
  const [processing, setProcessing] = useState(false)
  const [aiOutput, setAiOutput] = useState('')
  const [parsedData, setParsedData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [existingEntry, setExistingEntry] = useState(null)

  useEffect(()=>{ if(activeSite) setSelectedSite(activeSite.id) },[activeSite])
  useEffect(()=>{ if(selectedSite&&dateVal) checkExisting() },[selectedSite,dateVal])

  async function checkExisting() {
    const { data } = await supabase.from('diary_entries')
      .select('*').eq('site_id',selectedSite).eq('date',dateVal).maybeSingle()
    setExistingEntry(data||null)
  }

  async function imageToBase64(file) {
    return new Promise(resolve=>{
      const r=new FileReader(); r.onload=e=>resolve(e.target.result.split(',')[1]); r.readAsDataURL(file)
    })
  }

  async function handleProcess() {
    if (!text.trim()&&receiptImages.length===0) return
    if (!selectedSite) { alert('Select a site first'); return }
    setProcessing(true); setAiOutput(''); setParsedData(null)
    const apiKey = process.env.REACT_APP_ANTHROPIC_KEY
    const foundSite = sites.find(s=>s.id===selectedSite)
    let receiptContext='', receiptDataList=[]

    for (const img of receiptImages) {
      const b64 = await imageToBase64(img)
      try {
        const res = await fetch('https://api.anthropic.com/v1/messages',{
          method:'POST',
          headers:{'Content-Type':'application/json','x-api-key':apiKey,'anthropic-version':'2023-06-01','anthropic-dangerous-direct-browser-access':'true'},
          body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:500,messages:[{role:'user',content:[
            {type:'image',source:{type:'base64',media_type:img.type||'image/jpeg',data:b64}},
            {type:'text',text:'Extract receipt data as JSON only. Format: {"receipt_number":"","supplier":"","items":[{"name":"","qty":"","unit":"","unit_price":0,"subtotal":0}],"total":0}. Return JSON only, no markdown.'}
          ]}]})
        })
        const d=await res.json()
        const parsed=JSON.parse((d.content?.[0]?.text||'{}').replace(/```json|```/g,'').trim())
        receiptDataList.push(parsed); receiptContext+=`\nReceipt: ${JSON.stringify(parsed)}`
      } catch(e) { receiptContext+='\nReceipt: (could not parse)' }
    }

    const mergeContext = existingEntry
      ? `\nEXISTING ENTRY (merge intelligently, no duplicates):\n${existingEntry.ai_output||''}` : ''

    const systemPrompt = `You are a professional construction site diary AI for a contractor in Malaysia.
Rules:
1. Output in SAME language as input.
2. ONLY include sections with actual content.
3. Use clean markdown tables and bullet points.
4. If merging, combine intelligently - no duplicates.

Available sections (only relevant ones):
- Basic Info: date, site, weather
- 进度/Progress: area, work done, status
- 材料/Materials: material, qty, unit
- 采购明细/Purchases: receipt details
- 设备维修/Equipment: device, issue, action
- 业主指示/Client Instructions
- Testing/检验
- 安全备注/Safety Notes

Format:
## [ok] [SITE] - [DATE]

| 项目 | 详情 |
| 工地 | ... |

## [Section]
...

---
🔔 **风险提醒:** [1 line]`

    const dayNames=['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
    const userMsg=`Site: ${foundSite?.name||'Unknown'}\nDate: ${dateVal} (${dayNames[new Date(dateVal).getDay()]})\nInput: ${text}${receiptContext}${mergeContext}`

    try {
      const res=await fetch('https://api.anthropic.com/v1/messages',{
        method:'POST',
        headers:{'Content-Type':'application/json','x-api-key':apiKey,'anthropic-version':'2023-06-01','anthropic-dangerous-direct-browser-access':'true'},
        body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:1500,system:systemPrompt,messages:[{role:'user',content:userMsg}]})
      })
      const d=await res.json()
      const output=d.content?.[0]?.text||''
      setAiOutput(output)
      setParsedData({receiptDataList,siteId:selectedSite,date:dateVal,rawInput:text,siteName:foundSite?.name})
    } catch(e) { setAiOutput('Error: '+e.message) }
    setProcessing(false)
  }

  async function handleSave() {
    if (!aiOutput||!parsedData) return
    setSaving(true)
    const apiKey=process.env.REACT_APP_ANTHROPIC_KEY
    const {data:entry,error}=await supabase.from('diary_entries').upsert({
      site_id:parsedData.siteId, user_id:session.user.id, date:parsedData.date,
      raw_input:existingEntry?(existingEntry.raw_input||'')+'\n---\n'+parsedData.rawInput:parsedData.rawInput,
      ai_output:aiOutput, updated_at:new Date().toISOString()
    },{onConflict:'site_id,date'}).select().single()

    if (!error&&entry) {
      // Save receipt-image-based purchases
      for (const receipt of (parsedData.receiptDataList||[])) {
        await supabase.from('purchases').insert({
          diary_entry_id:entry.id, site_id:parsedData.siteId,
          receipt_number:receipt.receipt_number||null, supplier:receipt.supplier||null,
          items:receipt.items||[], total_amount:receipt.total||0
        })
      }
      // Also extract purchases from text input
      await extractAndSavePurchases(parsedData.siteId, entry.id, aiOutput, apiKey, parsedData.receiptDataList?.length > 0)
      for (let i=0;i<progressImages.length;i++) {
        const img=progressImages[i]
        const path=`${session.user.id}/${entry.id}/${Date.now()}_${img.name}`
        const {data:uploaded}=await supabase.storage.from('site-images').upload(path,img)
        if (uploaded) await supabase.from('entry_images').insert({diary_entry_id:entry.id,image_type:'progress',storage_path:path,caption:progressCaptions[i]||null})
      }
      await updateMaterialUsage(parsedData.siteId, aiOutput, apiKey, entry.id, false)
      setSaved(true)
      setText('');setReceiptImages([]);setProgressImages([]);setProgressCaptions([]);setAiOutput('');setParsedData(null)
      checkExisting(); onRefresh()
      setTimeout(()=>setSaved(false),3000)
    } else { alert('Save error: '+error?.message) }
    setSaving(false)
  }

  return (
    <div style={S.sec}>
      <div style={{marginBottom:28}}>
        <div style={S.pageTitle}>Daily Diary</div>
      </div>
      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        <div style={{flex:'2 1 160px'}}>
          <label style={S.lbl}>Site</label>
          <select value={selectedSite} onChange={e=>setSelectedSite(e.target.value)} style={S.input}>
            <option value=''>- select site -</option>
            {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{flex:'1 1 130px'}}>
          <label style={S.lbl}>Date</label>
          <input type='date' value={dateVal} onChange={e=>setDateVal(e.target.value)} style={S.input}/>
        </div>
      </div>
      {existingEntry&&(
        <div style={{fontSize:11,color:C.amber,padding:'8px 12px',background:'#f5a62308',border:`0.5px solid #f5a62322`,borderRadius:4,marginBottom:12,fontFamily:C.mono}}>
          ! entry exists - input will be merged
        </div>
      )}
      <div style={{marginBottom:12}}>
        <label style={S.lbl}>Input - describe freely</label>
        <textarea value={text} onChange={e=>setText(e.target.value)}
          placeholder='E.g.: poured 25m3 concrete, 6 skilled workers, 34°C, B-zone formwork cracked - fixed, bought 2 bags cement RM80...'
          style={S.textarea} rows={5}/>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
        <div style={S.uploadBox}>
          <label style={S.lbl}>Receipt images</label>
          <input type='file' accept='image/*' multiple onChange={e=>setReceiptImages(Array.from(e.target.files))} style={{fontSize:13,color:C.dim}}/>
          {receiptImages.length>0&&<div style={{fontSize:11,color:'#34d399',marginTop:6,fontFamily:C.mono}}>{receiptImages.length} file(s) - AI will extract</div>}
        </div>
        <div style={S.uploadBox}>
          <label style={S.lbl}>Progress images</label>
          <input type='file' accept='image/*' multiple onChange={e=>{
            const files=Array.from(e.target.files); setProgressImages(files); setProgressCaptions(files.map(()=>''))
          }} style={{fontSize:13,color:C.dim}}/>
          {progressImages.map((img,i)=>(
            <div key={i} style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:10,color:C.dim,flex:'1 1 80px',fontFamily:C.mono}}>{img.name}</span>
              <input placeholder='caption (optional)' value={progressCaptions[i]||''}
                onChange={e=>{const c=[...progressCaptions];c[i]=e.target.value;setProgressCaptions(c)}}
                style={{...S.input,flex:'2 1 140px',fontSize:12,padding:'6px 10px'}}/>
            </div>
          ))}
        </div>
      </div>
      <button onClick={handleProcess}
        disabled={(!text.trim()&&receiptImages.length===0)||processing||!selectedSite}
        style={{...S.btnAmber,width:'100%',justifyContent:'center',marginBottom:16}}>
        {processing?'processing...':'process'}
      </button>
      {aiOutput&&(
        <div style={{...S.card,borderColor:'#f5a62322'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,paddingBottom:12,borderBottom:`0.5px solid ${C.border}`,flexWrap:'wrap',gap:8}}>
            <span style={{fontFamily:C.mono,fontSize:9,color:C.amber,letterSpacing:'0.1em'}}>OUTPUT</span>
            <button onClick={handleSave} disabled={saving} style={S.btnGreen}>
              {saving?'saving...':'save to archive'}
            </button>
          </div>
          <div dangerouslySetInnerHTML={{__html:mdToHtml(aiOutput)}}/>
        </div>
      )}
      {saved&&<div style={{padding:'10px 14px',background:'#34d39908',border:'0.5px solid #34d39933',borderRadius:4,color:'#34d399',fontSize:12,textAlign:'center',marginTop:10,fontFamily:C.mono}}>
        saved - {sites.find(s=>s.id===selectedSite)?.name} {dateVal}
      </div>}
    </div>
  )
}

async function extractAndSavePurchases(siteId, entryId, diaryText, apiKey, hasReceiptImages) {
  // If receipt images were processed, they already handled purchase extraction
  // But we still extract text-based purchases that may not be covered by receipts
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 600,
        messages: [{ role: 'user', content: `Extract any purchase/procurement records from this construction site diary entry.

Look for: items bought, materials purchased, equipment hired, repairs paid, any spending mentioned with amounts.

Rules:
1. Only extract purchases that have a clear amount (RM or ringgit mentioned)
2. If no purchases found, return empty array
3. Group related items from the same supplier if possible
4. Return JSON only, no explanation

Diary:
${diaryText.slice(0, 1500)}

Return format: {"purchases":[{"supplier":"","items":[{"name":"","qty":"1","unit":"","unit_price":0,"subtotal":0}],"total":0,"receipt_number":""}]}
If no purchases: {"purchases":[]}` }]
      })
    })
    const d = await res.json()
    const parsed = JSON.parse((d.content?.[0]?.text || '{"purchases":[]}').replace(/```json|```/g, '').trim())

    for (const p of (parsed.purchases || [])) {
      if (!p.total || p.total <= 0) continue
      // Check if this purchase already exists (avoid duplicate from receipt image)
      if (hasReceiptImages && p.receipt_number) continue
      await supabase.from('purchases').insert({
        diary_entry_id: entryId,
        site_id: siteId,
        receipt_number: p.receipt_number || null,
        supplier: p.supplier || null,
        items: p.items || [],
        total_amount: p.total || 0
      })
    }
  } catch(e) {}
}

async function updateMaterialUsage(siteId, diaryText, apiKey, diaryEntryId, isEdit = false) {
  const { data: siteMats } = await supabase.from('site_materials').select('*').eq('site_id', siteId)
  if (!siteMats?.length) return

  try {
    // Extract quantities from diary
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 400,
        messages: [{ role: 'user', content: `You are extracting material usage quantities from a construction site diary entry.

Known materials to look for: ${siteMats.map(m=>m.material_name).join(', ')}

Rules:
1. Match diary mentions to known materials even if names differ slightly (e.g. "Prime Coat喷洒" = "Prime Coat", "ACB28 Binder1" = "ACB 28")
2. Extract the numeric quantity actually used (not planned totals)
3. For area in m2, use the calculated number (e.g. "2,800平方米" = 2800)
4. Only include materials with clear quantities mentioned
5. Return JSON only, no explanation

Diary entry:
${diaryText.slice(0, 1200)}

Return format: {"materials":[{"name":"exact name from known materials list","qty":0}]}` }]
      })
    })
    const d = await res.json()
    const parsed = JSON.parse((d.content?.[0]?.text || '{"materials":[]}').replace(/```json|```/g, '').trim())

    function normalize(s) { return s.toLowerCase().replace(/[\s\-_.]/g, '') }

    for (const item of (parsed.materials || [])) {
      const mat = siteMats.find(m =>
        normalize(m.material_name).includes(normalize(item.name)) ||
        normalize(item.name).includes(normalize(m.material_name)) ||
        normalize(m.material_name).startsWith(normalize(item.name).slice(0, 4)) ||
        normalize(item.name).startsWith(normalize(m.material_name).slice(0, 4))
      )
      if (!mat || item.qty <= 0) continue

      if (isEdit) {
        // Update existing log entry for this diary
        const { data: existingLog } = await supabase.from('material_usage_log')
          .select('*').eq('diary_entry_id', diaryEntryId).eq('site_material_id', mat.id).maybeSingle()

        if (existingLog) {
          await supabase.from('material_usage_log').update({ qty: item.qty }).eq('id', existingLog.id)
        } else {
          await supabase.from('material_usage_log').insert({
            site_id: siteId, site_material_id: mat.id,
            diary_entry_id: diaryEntryId, qty: item.qty
          })
        }
      } else {
        // New diary entry - insert log
        await supabase.from('material_usage_log').insert({
          site_id: siteId, site_material_id: mat.id,
          diary_entry_id: diaryEntryId, qty: item.qty
        })
      }

      // Recalculate used_qty from all logs for this material
      const { data: allLogs } = await supabase.from('material_usage_log')
        .select('qty').eq('site_material_id', mat.id)
      const totalUsed = (allLogs || []).reduce((sum, log) => sum + (log.qty || 0), 0)
      await supabase.from('site_materials').update({ used_qty: totalUsed }).eq('id', mat.id)
    }
  } catch(e) {}
}

function SitesTab({ sites, onRefresh, session }) {
  const [showNew, setShowNew] = useState(false)
  const [editingSite, setEditingSite] = useState(null)
  const [expandedSite, setExpandedSite] = useState(null)
  const [form, setForm] = useState({name:'',address:'',contract_amount:'',start_date:'',expected_end_date:'',status:'active'})
  const [materials, setMaterials] = useState([{material_name:'',planned_qty:'',unit:'ton'}])
  const [specsInput, setSpecsInput] = useState('')
  const [specsImages, setSpecsImages] = useState([])
  const [specsPdf, setSpecsPdf] = useState(null)
  const [specsProcessing, setSpecsProcessing] = useState(false)
  const [specsPreview, setSpecsPreview] = useState(null)
  const [saving, setSaving] = useState(false)

  function openEdit(siteData) {
    setEditingSite(siteData)
    setForm({name:siteData.name||'',address:siteData.address||'',contract_amount:siteData.contract_amount||'',start_date:siteData.start_date||'',expected_end_date:siteData.expected_end_date||'',status:siteData.status||'active'})
    setMaterials(siteData.site_materials?.length>0?siteData.site_materials.map(m=>({id:m.id,material_name:m.material_name,planned_qty:m.planned_qty,used_qty:m.used_qty||0,unit:m.unit})):[{material_name:'',planned_qty:'',unit:'ton'}])
    setSpecsInput(siteData.project_specs_raw||'')
    setSpecsPreview(siteData.project_specs||null)
    setShowNew(true)
  }

  function closeForm() {
    setShowNew(false); setEditingSite(null)
    setForm({name:'',address:'',contract_amount:'',start_date:'',expected_end_date:'',status:'active'})
    setMaterials([{material_name:'',planned_qty:'',unit:'ton'}])
    setSpecsInput(''); setSpecsImages([]); setSpecsPdf(null); setSpecsPreview(null)
  }

  async function processSpecs() {
    setSpecsProcessing(true)
    const apiKey = process.env.REACT_APP_ANTHROPIC_KEY
    try {
      let content = []
      if (specsImages.length > 0) {
        for (const img of specsImages) {
          const b64 = await new Promise(resolve => { const r=new FileReader(); r.onload=e=>resolve(e.target.result.split(',')[1]); r.readAsDataURL(img) })
          content.push({ type:'image', source:{ type:'base64', media_type:img.type||'image/jpeg', data:b64 } })
        }
      }
      if (specsPdf) {
        const b64 = await new Promise(resolve => { const r=new FileReader(); r.onload=e=>resolve(e.target.result.split(',')[1]); r.readAsDataURL(specsPdf) })
        content.push({ type:'document', source:{ type:'base64', media_type:'application/pdf', data:b64 } })
      }
      if (specsInput.trim()) content.push({ type:'text', text:`Project specs:\n${specsInput}` })
      if (content.length === 0) { setSpecsProcessing(false); return }
      content.push({ type:'text', text:`Extract construction project specifications organized by work item. Return JSON where each key is a work item and value is an object with its parameters (length_m, width_m, thickness_m, area_m2, etc). Include a "road" key for general parameters. Return JSON only, no markdown. Example: {"road":{"total_length_m":2450},"Prime Coat":{"length_m":1225,"width_m":8,"area_m2":19600}}` })
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{'Content-Type':'application/json','x-api-key':apiKey,'anthropic-version':'2023-06-01','anthropic-dangerous-direct-browser-access':'true'},
        body: JSON.stringify({ model:'claude-sonnet-4-20250514', max_tokens:800, messages:[{role:'user',content}] })
      })
      const d = await res.json()
      const parsed = JSON.parse((d.content?.[0]?.text||'{}').replace(/```json|```/g,'').trim())
      setSpecsPreview(parsed)
    } catch(e) { alert('Error: '+e.message) }
    setSpecsProcessing(false)
  }

  async function handleSave() {
    if (!form.name.trim()) return
    setSaving(true)
    const specsData = specsPreview || (specsInput.trim() ? { raw: specsInput } : null)
    if (editingSite) {
      const { error } = await supabase.from('sites').update({
        name:form.name, address:form.address, status:form.status,
        contract_amount:form.contract_amount?parseFloat(form.contract_amount):null,
        start_date:form.start_date||null, expected_end_date:form.expected_end_date||null,
        project_specs:specsData, project_specs_raw:specsInput||null,
      }).eq('id', editingSite.id)
      if (!error) {
        const existingIds=editingSite.site_materials?.map(m=>m.id)||[]
        const keptIds=materials.filter(m=>m.id).map(m=>m.id)
        const deletedIds=existingIds.filter(id=>!keptIds.includes(id))
        if (deletedIds.length>0) await supabase.from('site_materials').delete().in('id',deletedIds)
        for (const m of materials) {
          if (!m.material_name.trim()) continue
          if (m.id) await supabase.from('site_materials').update({material_name:m.material_name,planned_qty:parseFloat(m.planned_qty)||0,unit:m.unit}).eq('id',m.id)
          else await supabase.from('site_materials').insert({site_id:editingSite.id,material_name:m.material_name,planned_qty:parseFloat(m.planned_qty)||0,unit:m.unit,used_qty:0})
        }
        onRefresh(); closeForm()
      } else { alert('Error: '+error.message) }
    } else {
      const { data:newSite, error } = await supabase.from('sites').insert({
        name:form.name, address:form.address, status:form.status,
        contract_amount:form.contract_amount?parseFloat(form.contract_amount):null,
        start_date:form.start_date||null, expected_end_date:form.expected_end_date||null,
        user_id:session.user.id, project_specs:specsData, project_specs_raw:specsInput||null,
      }).select().single()
      if (!error&&newSite) {
        const valid=materials.filter(m=>m.material_name.trim()&&m.planned_qty)
        if (valid.length>0) await supabase.from('site_materials').insert(valid.map(m=>({site_id:newSite.id,material_name:m.material_name,planned_qty:parseFloat(m.planned_qty),unit:m.unit,used_qty:0})))
        onRefresh(); closeForm()
      } else { alert('Error: '+error?.message) }
    }
    setSaving(false)
  }

  const grouped = {
    active:   sites.filter(s=>s.status==='active'),
    pending:  sites.filter(s=>s.status==='pending'),
    complete: sites.filter(s=>s.status==='complete'),
  }
  const claimedSites = sites.filter(s=>s.status==='claimed')

  return (
    <div style={S.sec}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:28}}>
        <div style={S.pageTitle}>Sites</div>
        {!showNew && <button onClick={()=>setShowNew(true)} style={S.btnAmber}>+ new site</button>}
      </div>

      {showNew && (
        <div style={{...S.card,borderColor:'#f5a62322',marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
            <div style={{fontFamily:C.mono,fontSize:11,color:C.fg,letterSpacing:'0.06em'}}>
              {editingSite ? `editing - ${editingSite.name}` : 'new site'}
            </div>
            <button onClick={closeForm} style={{...S.btnGhost,padding:'4px 10px',fontSize:11}}>cancel</button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10,marginBottom:16}}>
            {[['Site name *','name','text'],['Address','address','text'],['Contract (RM)','contract_amount','number'],['Start date','start_date','date'],['End date','expected_end_date','date']].map(([label,key,type])=>(
              <div key={key}>
                <label style={S.lbl}>{label}</label>
                <input type={type} value={form[key]} onChange={e=>setForm({...form,[key]:e.target.value})} style={S.input}/>
              </div>
            ))}
            <div>
              <label style={S.lbl}>Status</label>
              <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})} style={S.input}>
                {SITE_STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{marginBottom:20}}>
            <div style={S.lbl}>Materials</div>
            {materials.map((m,i)=>(
              <div key={i} style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap',alignItems:'center'}}>
                <input placeholder="name" value={m.material_name} onChange={e=>{const n=[...materials];n[i].material_name=e.target.value;setMaterials(n)}} style={{...S.input,flex:'2 1 130px'}}/>
                <input placeholder="qty" type="number" value={m.planned_qty} onChange={e=>{const n=[...materials];n[i].planned_qty=e.target.value;setMaterials(n)}} style={{...S.input,flex:'1 1 70px'}}/>
                <select value={m.unit} onChange={e=>{const n=[...materials];n[i].unit=e.target.value;setMaterials(n)}} style={{...S.input,flex:'1 1 70px'}}>
                  <option value="ton">ton</option><option value="m3">m3</option><option value="m2">m2</option><option value="m">m</option>
                </select>
                {m.id && <span style={{fontFamily:C.mono,fontSize:10,color:C.dim,whiteSpace:'nowrap'}}>used: {m.used_qty||0}</span>}
                {materials.length>1 && <button onClick={()=>setMaterials(materials.filter((_,j)=>j!==i))} style={{...S.btnGhost,padding:'6px 8px',color:'#ef4444',borderColor:'#ef444433',fontSize:11}}>x</button>}
              </div>
            ))}
            <button onClick={()=>setMaterials([...materials,{material_name:'',planned_qty:'',unit:'ton'}])} style={{...S.btnGhost,fontSize:11}}>+ material</button>
          </div>

          <div style={{marginBottom:20,paddingTop:16,borderTop:`0.5px solid ${C.border}`}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
              <div style={S.lbl}>Project Specs</div>
              <span style={{fontFamily:C.mono,fontSize:9,color:C.dim}}>(optional)</span>
            </div>
            <textarea value={specsInput} onChange={e=>setSpecsInput(e.target.value)}
              placeholder='E.g.: Road length 2.3km, width 7m, AC thickness 50mm, shoulder 1.5m wide 150mm deep...'
              style={{...S.textarea,minHeight:80,marginBottom:10,fontSize:12}}/>
            <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
              <div style={{...S.uploadBox,flex:'1 1 180px'}}>
                <label style={S.lbl}>Photos / drawings</label>
                <input type='file' accept='image/*' multiple onChange={e=>setSpecsImages(Array.from(e.target.files))} style={{fontSize:12,color:C.dim}}/>
                {specsImages.length>0 && <div style={{fontSize:11,color:'#34d399',marginTop:4,fontFamily:C.mono}}>{specsImages.length} image(s)</div>}
              </div>
              <div style={{...S.uploadBox,flex:'1 1 180px'}}>
                <label style={S.lbl}>PDF (BQ / drawings)</label>
                <input type='file' accept='application/pdf' onChange={e=>setSpecsPdf(e.target.files[0]||null)} style={{fontSize:12,color:C.dim}}/>
                {specsPdf && <div style={{fontSize:11,color:'#34d399',marginTop:4,fontFamily:C.mono}}>{specsPdf.name}</div>}
              </div>
            </div>
            {(specsInput.trim()||specsImages.length>0||specsPdf) && (
              <button onClick={processSpecs} disabled={specsProcessing} style={{...S.btnGhost,fontSize:11,marginBottom:10}}>
                {specsProcessing ? 'extracting...' : 'extract specs with AI'}
              </button>
            )}
            {specsPreview && (
              <div style={{background:C.card2,border:`0.5px solid ${C.border}`,borderRadius:4,padding:12}}>
                <div style={{fontFamily:C.mono,fontSize:9,color:C.amber,letterSpacing:'0.1em',marginBottom:12}}>EXTRACTED SPECS</div>
                <SpecsDisplay specs={specsPreview}/>
                <button onClick={()=>setSpecsPreview(null)} style={{...S.btnGhost,fontSize:10,marginTop:10,padding:'4px 10px'}}>clear</button>
              </div>
            )}
          </div>

          <button onClick={handleSave} disabled={saving} style={{...S.btnAmber,width:'100%',justifyContent:'center'}}>
            {saving ? 'saving...' : editingSite ? 'save changes' : 'create site'}
          </button>
        </div>
      )}

      {Object.entries(grouped).map(([status,list]) => list.length===0 ? null : (
        <div key={status} style={{marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
            <StatusBadge status={status}/>
            <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{list.length} site{list.length>1?'s':''}</span>
          </div>
          {list.map(s => (
            <SiteRow key={s.id} siteData={s}
              expanded={expandedSite===s.id}
              onToggle={()=>setExpandedSite(expandedSite===s.id?null:s.id)}
              onEdit={()=>openEdit(s)}/>
          ))}
        </div>
      ))}

      {sites.filter(s=>s.status!=='claimed').length===0&&!showNew&&<div style={S.empty}>No sites yet.</div>}

      {/* Claimed — archived section */}
      {claimedSites.length > 0 && (
        <div style={{marginTop:28,paddingTop:20,borderTop:`0.5px solid ${C.border}`}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
            <div style={{fontFamily:C.mono,fontSize:9,color:'#a78bfa',letterSpacing:'0.1em'}}>CLAIMED — {claimedSites.length} site{claimedSites.length>1?'s':''}</div>
          </div>
          {claimedSites.map(s => (
            <div key={s.id} style={{...S.card,marginBottom:6,opacity:0.6}}>
              <div style={{display:'flex',alignItems:'center',gap:10,justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:5,height:5,borderRadius:'50%',background:'#a78bfa',flexShrink:0}}/>
                  <span style={{fontSize:13,color:C.fg2}}>{s.name}</span>
                  <StatusBadge status={s.status}/>
                </div>
                <button onClick={()=>openEdit(s)} style={{...S.btnGhost,padding:'4px 10px',fontSize:11}}>edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
function SpecsDisplay({ specs }) {
  const PARAM_CONFIG = {
    'length_m':            { label:'Length',   unit:'m' },
    'width_m':             { label:'Width',    unit:'m' },
    'thickness_m':         { label:'Thick',    unit:'m' },
    'area_m2':             { label:'Area',     unit:'m2' },
    'total_length_m':      { label:'Total L',  unit:'m' },
    'carriageway_width_m': { label:'Cway W',   unit:'m' },
    'depth_m':             { label:'Depth',    unit:'m' },
    'nos':                 { label:'Nos',      unit:'' },
    'segments':            { label:'Segments', unit:'' },
    'quantity':            { label:'Qty',      unit:'' },
  }

  const entries = Object.entries(specs).filter(function(e) { return e[0] !== 'raw' })

  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      {entries.map(function(e) {
        const group = e[0]
        const params = e[1]
        const isObj = params !== null && typeof params === 'object' && !Array.isArray(params)
        const paramEntries = isObj ? Object.entries(params) : []
        return (
          <div key={group} style={{borderBottom:'0.5px solid '+C.border,paddingBottom:12}}>
            <div style={{fontFamily:C.mono,fontSize:10,color:C.amber,letterSpacing:'0.06em',marginBottom:10}}>
              {group.toUpperCase()}
            </div>
            {isObj && paramEntries.length > 0 ? (
              <div style={{overflowX:'auto'}}>
                <div style={{display:'flex',gap:0,marginBottom:5,minWidth:'max-content'}}>
                  {paramEntries.map(function(pe) {
                    const k = pe[0]
                    const cfg = PARAM_CONFIG[k]
                    const label = cfg ? cfg.label : k.replace(/_/g,' ')
                    return (
                      <div key={k} style={{minWidth:80,width:80}}>
                        <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.08em',textTransform:'uppercase'}}>
                          {label}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div style={{display:'flex',gap:0,minWidth:'max-content'}}>
                  {paramEntries.map(function(pe) {
                    const k = pe[0]
                    const v = pe[1]
                    const cfg = PARAM_CONFIG[k]
                    const unit = cfg ? cfg.unit : ''
                    return (
                      <div key={k} style={{minWidth:80,width:80,display:'flex',alignItems:'baseline',gap:3}}>
                        <span style={{fontFamily:C.mono,fontSize:16,fontWeight:500,color:C.fg}}>{String(v)}</span>
                        {unit && <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{unit}</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <span style={{fontSize:12,color:C.fg2}}>{String(params)}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function SiteRow({ siteData, expanded, onToggle, onEdit }) {
  const mats = siteData.site_materials||[]
  const specs = siteData.project_specs

  // Summary: best material progress
  const activeMats = mats.filter(m=>m.planned_qty>0)
  const avgPct = activeMats.length > 0
    ? Math.round(activeMats.reduce((s,m)=>s+Math.min(100,m.used_qty/m.planned_qty*100),0)/activeMats.length)
    : null

  return (
    <div style={{...S.card,marginBottom:6,padding:0,overflow:'hidden'}}>
      {/* Row header - always visible */}
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',cursor:'pointer'}}
        onClick={onToggle}>
        <div style={{width:5,height:5,borderRadius:'50%',background:expanded?C.amber:C.dim,flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span style={{fontSize:13,fontWeight:500,color:C.fg}}>{siteData.name}</span>
            <StatusBadge status={siteData.status}/>
            {siteData.address && <span style={{fontSize:11,color:C.dim}}>{siteData.address}</span>}
          </div>
          {/* Mini progress bar if has materials */}
          {activeMats.length > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6}}>
              <div style={{flex:1,maxWidth:120,height:2,background:C.border,borderRadius:1}}>
                <div style={{height:'100%',background:avgPct>=80?C.amber:'#34d399',borderRadius:1,width:`${avgPct}%`}}/>
              </div>
              <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{avgPct}% avg</span>
              <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>{activeMats.length} material{activeMats.length>1?'s':''}</span>
            </div>
          )}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
          {siteData.contract_amount && (
            <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>
              RM {(siteData.contract_amount/1000000).toFixed(1)}M
            </span>
          )}
          {siteData.expected_end_date && (
            <span style={{fontFamily:C.mono,fontSize:10,color:C.dim}}>> {siteData.expected_end_date}</span>
          )}
          <button onClick={e=>{e.stopPropagation();onEdit()}}
            style={{...S.btnGhost,padding:'4px 10px',fontSize:11}}>edit</button>
          <span style={{fontFamily:C.mono,fontSize:12,color:C.dim}}>{expanded?'-':'+'}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{borderTop:`0.5px solid ${C.border}`,padding:'14px 16px'}}>

          {/* Materials detail */}
          {mats.length > 0 && (
            <div style={{marginBottom:specs?16:0}}>
              <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em',marginBottom:10}}>MATERIALS</div>
              {mats.map(m=>{
                const pct=m.planned_qty>0?Math.min(100,Math.round(m.used_qty/m.planned_qty*100)):0
                const remaining=m.planned_qty-m.used_qty
                return (
                  <div key={m.id} style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3,flexWrap:'wrap',gap:4}}>
                      <span style={{color:C.fg2}}>{m.material_name}</span>
                      <span style={{fontFamily:C.mono,color:C.dim}}>
                        {m.used_qty}/{m.planned_qty} {m.unit}{' '}
                        <span style={{color:remaining<0?'#ef4444':'#34d399'}}>{remaining>=0?'+':''}{remaining}</span>
                      </span>
                    </div>
                    <div style={{height:2,background:C.border,borderRadius:1}}>
                      <div style={{height:'100%',background:pct>=100?'#ef4444':pct>=80?C.amber:'#34d399',borderRadius:1,width:`${pct}%`}}/>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Project Specs */}
          {specs && Object.keys(specs).filter(k=>k!=='raw').length > 0 && (
            <div>
              <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em',marginBottom:10}}>PROJECT SPECS</div>
              <SpecsDisplay specs={specs}/>
            </div>
          )}

          {/* No data */}
          {mats.length===0 && !specs && (
            <div style={{fontSize:11,color:C.dim,fontFamily:C.mono}}>No materials or specs - click edit to add.</div>
          )}
        </div>
      )}
    </div>
  )
}
function QSTab({ sites, activeSite }) {
  const [qTab, setQTab] = useState('active')
  const [qSite, setQSite] = useState(activeSite?.id||'')
  const [qFrom, setQFrom] = useState('')
  const [qTo, setQTo] = useState('')
  const [purchases, setPurchases] = useState([])

  useEffect(()=>{ if(activeSite) setQSite(activeSite.id) },[activeSite])
  useEffect(()=>{ loadPurchases() },[qSite,qFrom,qTo,qTab])

  async function loadPurchases() {
    let q=supabase.from('purchases').select('*, sites(name)').order('created_at',{ascending:false})
    if (qSite) q=q.eq('site_id',qSite)
    const {data}=await q; setPurchases(data||[])
  }

  const activeSites = sites.filter(s=>s.status!=='claimed')
  const claimedSites = sites.filter(s=>s.status==='claimed')
  const displaySites = qTab==='active' ? activeSites : claimedSites

  const foundSite=sites.find(s=>s.id===qSite)
  const mats=foundSite?.site_materials||[]
  const total=purchases.reduce((s,p)=>s+(p.total_amount||0),0)

  const PurchaseList = () => (
    <>
      {mats.length>0&&(
        <div style={{...S.card,marginBottom:12}}>
          <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em',marginBottom:14}}>MATERIAL PROGRESS</div>
          {mats.map(m=>{
            const pct=m.planned_qty>0?Math.min(100,Math.round(m.used_qty/m.planned_qty*100)):0
            const remaining=m.planned_qty-m.used_qty
            return (
              <div key={m.id} style={{marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,flexWrap:'wrap',gap:4}}>
                  <span style={{fontSize:12,fontWeight:500,color:C.fg}}>{m.material_name}</span>
                  <div style={{fontFamily:C.mono,fontSize:10,color:C.dim,display:'flex',gap:12}}>
                    <span>used <span style={{color:C.amber}}>{m.used_qty}</span></span>
                    <span>plan {m.planned_qty} {m.unit}</span>
                    <span style={{color:remaining<0?'#ef4444':'#34d399'}}>{remaining>=0?'+':''}{remaining} left</span>
                  </div>
                </div>
                <div style={{height:2,background:C.border,borderRadius:1}}>
                  <div style={{height:'100%',background:pct>=100?'#ef4444':pct>=80?C.amber:'#34d399',borderRadius:1,width:`${pct}%`}}/>
                </div>
                <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,marginTop:3}}>{pct}%</div>
              </div>
            )
          })}
        </div>
      )}
      <div style={S.card}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:14}}>
          <div style={{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.1em'}}>PURCHASES</div>
          <div style={{fontFamily:C.mono,fontSize:13,color:'#34d399'}}>RM {total.toLocaleString()}</div>
        </div>
        {purchases.length===0?<div style={S.empty}>No records.</div>:purchases.map(p=>(
          <div key={p.id} style={{marginBottom:14,paddingBottom:14,borderBottom:`0.5px solid ${C.border}`}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:4}}>
              <div>
                <span style={{fontSize:12,fontWeight:500,color:C.fg}}>{p.supplier||'Unknown'}</span>
                {p.receipt_number&&<span style={{fontFamily:C.mono,fontSize:10,color:C.dim,marginLeft:8}}>#{p.receipt_number}</span>}
                <div style={{fontSize:10,color:C.dim,marginTop:2}}>{p.sites?.name}</div>
              </div>
              <span style={{fontFamily:C.mono,color:'#34d399',fontWeight:500}}>RM {(p.total_amount||0).toLocaleString()}</span>
            </div>
            {(p.items||[]).length>0&&(
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                  <thead><tr>{['Item','Qty','Unit price','Subtotal'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.08em',borderBottom:`0.5px solid ${C.border}`}}>{h.toUpperCase()}</th>)}</tr></thead>
                  <tbody>{p.items.map((item,i)=>(
                    <tr key={i}>
                      <td style={{padding:'6px 8px',color:C.fg2,borderBottom:`0.5px solid ${C.border}`}}>{item.name}</td>
                      <td style={{padding:'6px 8px',color:C.fg2,borderBottom:`0.5px solid ${C.border}`,fontFamily:C.mono}}>{item.qty}</td>
                      <td style={{padding:'6px 8px',color:C.fg2,borderBottom:`0.5px solid ${C.border}`,fontFamily:C.mono}}>RM {item.unit_price}</td>
                      <td style={{padding:'6px 8px',color:C.fg2,borderBottom:`0.5px solid ${C.border}`,fontFamily:C.mono}}>RM {item.subtotal}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  )

  return (
    <div style={S.sec}>
      <div style={{marginBottom:20}}>
        <div style={S.pageTitle}>QS Report</div>
      </div>

      {/* Tab switcher */}
      <div style={{display:'flex',gap:4,marginBottom:20,background:C.card2,padding:4,borderRadius:6,border:`0.5px solid ${C.border}`}}>
        <button onClick={()=>{setQTab('active');setQSite('')}} style={{
          flex:1,padding:'8px',borderRadius:4,border:'none',cursor:'pointer',
          fontFamily:C.mono,fontSize:11,letterSpacing:'0.04em',
          background:qTab==='active'?C.card:'transparent',
          color:qTab==='active'?C.fg:C.dim
        }}>Active Sites</button>
        <button onClick={()=>{setQTab('claimed');setQSite('')}} style={{
          flex:1,padding:'8px',borderRadius:4,border:'none',cursor:'pointer',
          fontFamily:C.mono,fontSize:11,letterSpacing:'0.04em',
          background:qTab==='claimed'?C.card:'transparent',
          color:qTab==='claimed'?'#a78bfa':C.dim
        }}>
          Claimed History {claimedSites.length>0&&`(${claimedSites.length})`}
        </button>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        <select value={qSite} onChange={e=>setQSite(e.target.value)} style={{...S.input,flex:'1 1 140px'}}>
          <option value=''>All {qTab==='claimed'?'claimed':'active'} sites</option>
          {displaySites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type='date' value={qFrom} onChange={e=>setQFrom(e.target.value)} style={{...S.input,flex:'1 1 120px'}}/>
        <input type='date' value={qTo} onChange={e=>setQTo(e.target.value)} style={{...S.input,flex:'1 1 120px'}}/>
      </div>

      {qTab==='claimed'&&claimedSites.length===0 ? (
        <div style={S.empty}>No claimed sites yet.</div>
      ) : (
        <PurchaseList/>
      )}
    </div>
  )
}

function AITab({ sites, activeSite }) {
  const [msgs, setMsgs] = useState([{role:'assistant',content:`Hello. I'm your SiteAI manager.\n\nAsk me anything:\n- "How much concrete has Durin Bridge used?"\n- "Show all purchases this month"\n- "Which site has highest spend?"\n- "Summarize all active sites"`}])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(false)
  const [fSite, setFSite] = useState(activeSite?.id||'')
  const chatRef = useRef(null)

  useEffect(()=>{ chatRef.current?.scrollTo(0,chatRef.current.scrollHeight) },[msgs])
  useEffect(()=>{ if(activeSite) setFSite(activeSite.id) },[activeSite])

  async function send() {
    if (!q.trim()||loading) return
    const query=q
    setMsgs(m=>[...m,{role:'user',content:query}]); setQ(''); setLoading(true)
    let dQ=supabase.from('diary_entries').select('*, sites(name)').order('date',{ascending:false}).limit(20)
    if (fSite) dQ=dQ.eq('site_id',fSite)
    const {data:diaries}=await dQ
    let pQ=supabase.from('purchases').select('*, sites(name)').limit(30)
    if (fSite) pQ=pQ.eq('site_id',fSite)
    const {data:purchaseData}=await pQ
    const siteData=fSite?sites.filter(s=>s.id===fSite):sites.filter(s=>s.status==='active'||s.status==='pending')
    const context=`Sites: ${JSON.stringify(siteData.map(s=>({name:s.name,status:s.status,contract:s.contract_amount,materials:s.site_materials})))}\nDiaries: ${JSON.stringify((diaries||[]).map(d=>({site:d.sites?.name,date:d.date,content:d.ai_output?.slice(0,300)})))}\nPurchases: ${JSON.stringify((purchaseData||[]).map(p=>({site:p.sites?.name,supplier:p.supplier,total:p.total_amount})))}`
    const apiKey=process.env.REACT_APP_ANTHROPIC_KEY
    try {
      const res=await fetch('https://api.anthropic.com/v1/messages',{
        method:'POST',
        headers:{'Content-Type':'application/json','x-api-key':apiKey,'anthropic-version':'2023-06-01','anthropic-dangerous-direct-browser-access':'true'},
        body:JSON.stringify({model:'claude-sonnet-4-20250514',max_tokens:800,system:`You are a professional construction site AI manager. Answer based ONLY on provided data. Be concise, use tables when helpful. Match user language (Chinese or English).`,messages:[...msgs.slice(1).map(m=>({role:m.role,content:m.content})),{role:'user',content:`Data:\n${context}\n\nQuestion: ${query}`}]})
      })
      const d=await res.json()
      setMsgs(m=>[...m,{role:'assistant',content:d.content?.[0]?.text||'Error'}])
    } catch(e) { setMsgs(m=>[...m,{role:'assistant',content:'Error: '+e.message}]) }
    setLoading(false)
  }

  return (
    <div style={S.sec}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:28,flexWrap:'wrap',gap:8}}>
        <div style={S.pageTitle}>AI Manager</div>
        <select value={fSite} onChange={e=>setFSite(e.target.value)} style={{...S.input,width:'auto',minWidth:140,fontSize:12}}>
          <option value=''>All sites</option>
          {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div ref={chatRef} style={{background:C.card,border:`0.5px solid ${C.border}`,borderRadius:6,padding:16,height:400,overflowY:'auto',display:'flex',flexDirection:'column',gap:10,marginBottom:10,WebkitOverflowScrolling:'touch'}}>
        {msgs.map((m,i)=>(
          <div key={i} style={{maxWidth:'88%',padding:'10px 14px',borderRadius:4,fontSize:12,
            ...(m.role==='user'
              ? {alignSelf:'flex-end',background:'#1a1a1a',border:`0.5px solid ${C.border}`,color:C.fg}
              : {alignSelf:'flex-start',background:'#111',border:`0.5px solid ${C.border}`}
            )}}>
            {m.role==='assistant'&&<div style={{fontFamily:C.mono,fontSize:9,color:C.amber,letterSpacing:'0.1em',marginBottom:6}}>AI</div>}
            <div dangerouslySetInnerHTML={{__html:mdToHtml(m.content)}}/>
          </div>
        ))}
        {loading&&(
          <div style={{alignSelf:'flex-start',padding:'10px 14px',background:'#111',border:`0.5px solid ${C.border}`,borderRadius:4}}>
            <div style={{fontFamily:C.mono,fontSize:9,color:C.amber,letterSpacing:'0.1em',marginBottom:6}}>AI</div>
            <div style={{display:'flex',gap:4}}>
              {[0,1,2].map(i=><span key={i} style={{width:5,height:5,background:C.dim,borderRadius:'50%',display:'inline-block'}}/>)}
            </div>
          </div>
        )}
      </div>
      <div style={{display:'flex',gap:8}}>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()}
          placeholder='Ask anything...' style={{...S.input,flex:1}}/>
        <button onClick={send} disabled={!q.trim()||loading} style={S.btnAmber}>send</button>
      </div>
    </div>
  )
}

function mdToHtml(text) {
  if (!text) return ''
  const lines=text.split('\n'); const out=[]
  for (const line of lines) {
    const t=line.trim()
    if (/^\|[\s|:-]+\|$/.test(t)) continue
    if (t.startsWith('|')&&t.endsWith('|')&&t.split('|').slice(1,-1).every(c=>/^[\s:-]+$/.test(c))) continue
    if (/^## /.test(t)) {
      out.push(`<div style="color:#f5a623;font-weight:500;font-size:12px;letter-spacing:0.06em;margin:14px 0 6px;padding-bottom:4px;border-bottom:0.5px solid #1f1f1f;font-family:'JetBrains Mono',monospace">${t.replace(/^## /,'')}</div>`)
    } else if (/^### /.test(t)) {
      out.push(`<div style="color:#888;font-size:11px;font-family:'JetBrains Mono',monospace;letter-spacing:0.06em;margin:10px 0 4px;text-transform:uppercase">${t.replace(/^### /,'')}</div>`)
    } else if (/^> /.test(t)) {
      out.push(`<div style="border-left:2px solid #f5a623;padding:6px 10px;background:#f5a62308;margin:6px 0;color:#f5a623;font-size:12px;">${t.replace(/^> /,'')}</div>`)
    } else if (/^[-*] /.test(t)) {
      out.push(`<div style="padding:2px 0 2px 12px;color:#888;font-size:12px">- ${t.replace(/^[-*] /,'').replace(/\*\*(.*?)\*\*/g,'<strong style="color:#e0e0e0">$1</strong>')}</div>`)
    } else if (t.startsWith('|')) {
      const cells=t.split('|').map(c=>c.trim()).filter(Boolean)
      out.push(`<div style="display:flex;border-bottom:0.5px solid #1a1a1a;flex-wrap:wrap">${cells.map((c,i)=>`<span style="flex:${i===0?'0 0 110px':'1 1 80px'};padding:5px 8px;font-size:11px;color:${i===0?'#555':'#c0c0c0'};min-width:50px;font-family:${i===0?"'JetBrains Mono',monospace":'inherit'}">${c.replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>')}</span>`).join('')}</div>`)
    } else if (t==='---'||t==='***') {
      out.push(`<hr style="border:none;border-top:0.5px solid #1f1f1f;margin:10px 0"/>`)
    } else if (t) {
      out.push(`<div style="padding:2px 0;font-size:12px;color:#888;line-height:1.7">${t.replace(/\*\*(.*?)\*\*/g,'<strong style="color:#e0e0e0">$1</strong>')}</div>`)
    } else {
      out.push(`<div style="height:5px"></div>`)
    }
  }
  return out.join('')
}

const C = {
  bg:     '#0a0a0a',
  card:   '#111111',
  card2:  '#161616',
  border: '#1f1f1f',
  fg:     '#f0f0f0',
  fg2:    '#c0c0c0',
  dim:    '#555555',
  amber:  '#f5a623',
  mono:   "'JetBrains Mono', 'SF Mono', monospace",
}

const S = {
  authWrap:{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:C.bg,padding:20},
  authBox:{background:C.card,border:`0.5px solid ${C.border}`,borderRadius:8,padding:36,width:'100%',maxWidth:380},
  authMark:{display:'flex',justifyContent:'center',marginBottom:20},
  authMarkInner:{width:32,height:32,border:`1.5px solid ${C.amber}`,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center'},
  authTitle:{fontFamily:C.mono,fontSize:22,fontWeight:600,letterSpacing:'0.08em',textAlign:'center',marginBottom:4,color:C.fg},
  authSub:{fontSize:11,color:C.dim,textAlign:'center',letterSpacing:'0.12em',marginBottom:28,fontFamily:C.mono},
  authFields:{display:'flex',flexDirection:'column',gap:14},
  fieldWrap:{display:'flex',flexDirection:'column',gap:6},
  fieldLabel:{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.12em',textTransform:'uppercase'},
  authInput:{padding:'11px 12px',background:C.card2,border:`0.5px solid ${C.border}`,borderRadius:4,color:C.fg,fontSize:14,outline:'none',width:'100%',boxSizing:'border-box'},
  authError:{fontSize:11,color:'#ef4444',padding:'8px 12px',background:'#ef444408',borderRadius:4,border:'0.5px solid #ef444422',fontFamily:C.mono},
  authBtn:{padding:'12px',background:C.amber,border:'none',borderRadius:4,color:'#000',fontSize:13,fontWeight:600,cursor:'pointer',width:'100%',fontFamily:C.mono,letterSpacing:'0.06em'},
  authSwitch:{background:'transparent',border:'none',color:C.dim,fontSize:11,cursor:'pointer',textAlign:'center',padding:'4px',fontFamily:C.mono},
  splash:{height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:C.bg},
  appDesktop:{display:'flex',minHeight:'100vh',background:C.bg},
  appMobile:{display:'flex',flexDirection:'column',minHeight:'100vh',background:C.bg},
  sidebar:{width:220,flexShrink:0,background:C.card,borderRight:`0.5px solid ${C.border}`,display:'flex',flexDirection:'column',height:'100vh',position:'sticky',top:0,overflowY:'auto'},
  sidebarLogo:{display:'flex',alignItems:'center',gap:10,padding:'20px 16px',borderBottom:`0.5px solid ${C.border}`},
  sidebarSection:{padding:'16px 10px 8px'},
  sidebarLabel:{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.12em',marginBottom:6,paddingLeft:8},
  sidebarNav:{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'8px',background:'transparent',border:'none',borderRadius:4,color:C.dim,cursor:'pointer',fontSize:12,textAlign:'left',marginBottom:1,fontFamily:'Geist,-apple-system,sans-serif'},
  sidebarNavActive:{color:C.fg,background:C.card2},
  navDot:{width:4,height:4,borderRadius:'50%',background:C.dim,flexShrink:0},
  sidebarSite:{display:'flex',alignItems:'flex-start',gap:8,width:'100%',padding:'7px 8px',background:'transparent',border:'none',borderRadius:4,cursor:'pointer',textAlign:'left',marginBottom:1},
  sidebarSiteActive:{background:C.card2},
  sidebarBottom:{padding:'14px',borderTop:`0.5px solid ${C.border}`},
  sidebarLogout:{width:'100%',padding:'8px',background:'transparent',border:`0.5px solid ${C.border}`,borderRadius:4,color:C.dim,fontSize:11,cursor:'pointer',fontFamily:C.mono,letterSpacing:'0.06em'},
  desktopMain:{flex:1,overflowY:'auto',minWidth:0},
  desktopContent:{maxWidth:700,margin:'0 auto',padding:'0 0 80px'},
  mobileHeader:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:C.card,borderBottom:`0.5px solid ${C.border}`,position:'sticky',top:0,zIndex:100},
  mobileHeaderSelect:{padding:'6px 8px',background:C.card2,border:`0.5px solid ${C.border}`,borderRadius:4,color:C.fg,fontSize:11,outline:'none',maxWidth:150,fontFamily:C.mono},
  mobileNav:{display:'flex',position:'fixed',bottom:0,left:0,right:0,background:C.card,borderTop:`0.5px solid ${C.border}`,zIndex:100},
  mobileNavBtn:{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'8px 2px',background:'transparent',border:'none',color:C.dim,cursor:'pointer'},
  mobileNavActive:{color:C.amber},
  mobileMain:{flex:1,overflowY:'auto',paddingBottom:70},
  sec:{padding:'24px 20px'},
  pageTitle:{fontSize:20,fontWeight:500,color:C.fg,letterSpacing:'-0.01em'},
  lbl:{fontFamily:C.mono,fontSize:9,color:C.dim,letterSpacing:'0.12em',textTransform:'uppercase',display:'block',marginBottom:5},
  input:{width:'100%',padding:'9px 11px',background:C.card2,border:`0.5px solid ${C.border}`,borderRadius:4,color:C.fg,fontSize:13,outline:'none',boxSizing:'border-box'},
  textarea:{width:'100%',padding:'11px',background:C.card2,border:`0.5px solid ${C.border}`,borderRadius:4,color:C.fg,fontSize:13,resize:'vertical',outline:'none',lineHeight:1.7,fontFamily:'Geist,-apple-system,sans-serif',boxSizing:'border-box',minHeight:120},
  card:{background:C.card,border:`0.5px solid ${C.border}`,borderRadius:6,padding:16,marginBottom:10},
  cardTitle:{fontSize:13,fontWeight:500,color:C.fg,marginBottom:14},
  statCard:{background:C.card,border:`0.5px solid ${C.border}`,borderRadius:6,padding:'14px 16px'},
  btnAmber:{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,padding:'9px 18px',background:C.amber,border:'none',borderRadius:4,color:'#000',cursor:'pointer',fontSize:12,fontWeight:600,fontFamily:C.mono,letterSpacing:'0.04em',minHeight:38},
  btnGhost:{padding:'8px 14px',background:'transparent',border:`0.5px solid ${C.border}`,borderRadius:4,color:C.dim,cursor:'pointer',fontSize:12,minHeight:38,fontFamily:C.mono},
  btnGreen:{display:'inline-flex',alignItems:'center',gap:6,padding:'9px 16px',background:'transparent',border:'0.5px solid #34d39944',borderRadius:4,color:'#34d399',cursor:'pointer',fontSize:12,fontFamily:C.mono,minHeight:38},
  mergeAlert:{fontSize:11,color:C.amber,padding:'8px 12px',background:'#f5a62308',border:`0.5px solid #f5a62322`,borderRadius:4,marginBottom:12,fontFamily:C.mono},
  uploadBox:{padding:'12px',background:C.card2,border:`0.5px dashed ${C.border}`,borderRadius:4},
  empty:{color:C.dim,fontSize:12,padding:'40px 0',textAlign:'center',fontFamily:C.mono},
}
